import React, { useEffect, useMemo, useState } from 'react';

import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import uuid from 'uuid/v1';

import { BlueCircleCheckmark } from 'assets/icons/blue-circle-checkmark';
import { EyeIcon } from 'assets/icons/eye-icon';
import { CaretDown } from 'src/app/components/icons';
import { Button } from 'src/app/components/lib/button';
import { ControlBar, ControlGroup, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { Heading } from 'src/app/components/lib/heading';
import { PageLoader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import {
  FooterSelect,
  PageUnits,
  PaginationControlContainer,
  PaginationRange,
  StatusContainer,
} from 'src/app/components/lib/table/footer/footer-rebrand.styles';
import { RebrandPagination } from 'src/app/components/lib/table/footer/rebrand-pagination';
import { Type } from 'src/app/components/lib/type';
import { doesTemplateHaveContent, useDebounce } from 'src/app/pages/marketing/configure/marketing-templates/helpers';
import {
  HtmlPreviewIframe,
  RightSideControls,
  TemplateName,
  TemplatePreview,
} from 'src/app/pages/marketing/configure/marketing-templates/list/template-list.styles';
import { SlideContainer } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/shared/styles';
import { useGetDutchieTemplateQuery } from 'src/app/queries/email-marketing/dutchie-template';
import { useGetTemplatesQuery } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';

import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { getPayloadNodesWithUpdatedNode, getSaveWorkflowCampaignPayload } from '../../../wizard/helpers';
import { emailEditorNavigationState, isEmailEditorEnabled } from '../atoms/workflow-and-email-editor-atoms';

import { TemplatePreviewModal } from './template-preview-modal';

import type { ActionNodeData, WorkflowCampaignNode } from '../models';
import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';

export function WorkflowsTemplateSelect() {
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const debouncedSearchFilter = useDebounce(searchFilter, 1000);

  const { mutateAsync: saveWorkflowCampaign } = useSaveWorkflowCampaignMutation();

  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { selectedNodeId, selectedNode } = builderState;

  const [showEditor, setShowEditor] = useRecoilState(isEmailEditorEnabled);
  const [navState, setNavState] = useRecoilState(emailEditorNavigationState);
  const { isEditorLoading } = navState;

  useEffect(() => {
    if (showEditor) {
      setNavState((prev) => ({
        ...prev,
        hideWizardBottomNavigation: true,
      }));
    }
  }, [showEditor, setNavState]);

  const nodeData = selectedNode?.data as ActionNodeData;
  const savedTemplateId = nodeData?.config?.savedTemplateId;

  const setSelectedTemplatePreset = (campaignTemplate: MarketingTemplatePreset | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId ?? '',
        value: {
          config: {
            ...nodeData?.config,
            selectedTemplatePreset: campaignTemplate,
          },
        },
      },
    });
  };

  const setEmailMarketingTemplatePresetId = (marketingTemplatePresetId: number | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId ?? '',
        value: {
          config: {
            ...nodeData?.config,
            emailConfig: {
              ...nodeData?.config.emailConfig,
              marketingTemplatePresetId,
            },
          },
        },
      },
    });
  };

  const setSmsMarketingTemplatePresetId = (marketingTemplatePresetId: number | null) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId ?? '',
        value: {
          config: {
            ...nodeData?.config,
            smsConfig: {
              ...nodeData?.config.smsConfig,
              marketingTemplatePresetId,
            },
          },
        },
      },
    });
  };

  const {
    data: campaignTemplates = { Data: [], Page: 0, TotalPages: 0, TotalRows: 0 },
    isLoading: isCampaignTemplatesLoading,
  } = useGetTemplatesQuery({
    CurrentPage: currentPage,
    PageSize: pageSize,
    Sorting: [],
    Filters:
      debouncedSearchFilter.length > 0
        ? [
            {
              Key: 'TemplateName',
              Operator: 'Like',
              Value: debouncedSearchFilter,
            },
          ]
        : [],
  });
  const { data: preExistingTemplates = [], isLoading: isPreExistingTemplatesLoading } = useGetDutchieTemplateQuery();

  // Combine templates and campaign templates for first page only, adding UUIDs to templates
  const rows = useMemo(() => {
    if (currentPage === 0) {
      // On first page, show templates first, then campaign templates
      // Filter pre-existing templates based on search term
      const filteredPreExistingTemplates =
        debouncedSearchFilter.length > 0
          ? preExistingTemplates?.filter((template) =>
              template.TemplateName.toLowerCase().includes(debouncedSearchFilter.toLowerCase())
            ) ?? []
          : preExistingTemplates ?? [];

      const preExistingTemplateData = filteredPreExistingTemplates.map((template) => ({
        ...template,
        Id: template.Id || uuid(), // Add UUID if not already present
      }));
      const campaignTemplateData =
        campaignTemplates.Data?.map((template) => ({
          ...template,
          Id: template.Id || uuid(), // Add UUID if not already present
        })) ?? [];
      return [...preExistingTemplateData, ...campaignTemplateData];
    }
    // On other pages, only show campaign templates with UUIDs
    return (
      campaignTemplates.Data?.map((template) => ({
        ...template,
        Id: template.Id || uuid(), // Add UUID if not already present
      })) ?? []
    );
  }, [currentPage, preExistingTemplates, campaignTemplates.Data, debouncedSearchFilter]);

  const handleSearchFilterChange = (value: string) => {
    setSearchFilter(value);
    setCurrentPage(0); // Reset to first page when search changes
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  const [selectedListItem, setSelectedListItem] = useState<string | null>(null);
  const selectedTemplateHTML = useMemo(() => {
    const preExistingAndCampaignTemplates = rows.find((t) => t.Id === selectedListItem);
    return preExistingAndCampaignTemplates?.HtmlBody;
  }, [selectedListItem, rows]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleItemClick = (template: MarketingTemplatePreset | null) => {
    // Don't allow selection of templates without HTML/JSON content
    if (!doesTemplateHaveContent(template)) {
      return;
    }

    setSelectedListItem(template?.Id ?? null);

    // builder context
    if (nodeData.config.emailConfig) {
      setEmailMarketingTemplatePresetId(template?.MarketingTemplatePresetId ?? null);
    }

    if (nodeData.config.smsConfig) {
      setSmsMarketingTemplatePresetId(template?.MarketingTemplatePresetId ?? null);
    }
    setSelectedTemplatePreset(template);
  };

  const handleStartOver = async () => {
    // First update the node data to set savedTemplateId to null
    if (selectedNode && nodeData) {
      const updatedNode: WorkflowCampaignNode = {
        ...selectedNode,
        data: {
          ...nodeData,
          config: {
            ...nodeData.config,
            savedTemplateId: null,
            emailConfig: nodeData?.config.emailConfig?.isActive
              ? {
                  ...nodeData.config.emailConfig,
                  marketingTemplatePresetId: null,
                }
              : null,
            smsConfig: nodeData?.config.smsConfig?.isActive
              ? {
                  ...nodeData.config.smsConfig,
                  marketingTemplatePresetId: null,
                }
              : null,
          },
        },
      };

      // Then save the workflow campaign with the updated state
      await saveWorkflowCampaign(
        getSaveWorkflowCampaignPayload(
          {
            Nodes: getPayloadNodesWithUpdatedNode(builderState, updatedNode),
          },
          builderState
        )
      );
      setSelectedListItem(null);
    }
  };

  useEffect(() => {
    if (selectedListItem || savedTemplateId) {
      setNavState((prev) => ({
        ...prev,
        isNextButtonEnabled: true,
      }));
    } else {
      setNavState((prev) => ({
        ...prev,
        isNextButtonEnabled: false,
      }));
    }
  }, [selectedListItem, savedTemplateId, setNavState]);

  const handleContinueEditing = () => {
    setNavState((prev) => ({
      ...prev,
      currentStep: 1,
    }));
  };

  const handleCloseEditor = () => {
    setShowEditor(false);

    setNavState((prev) => ({
      ...prev,
      hideWizardBottomNavigation: false,
    }));

    // not necessary but maybe a good safeguard to have
    setNavState((prev) => ({
      ...prev,
      currentStep: 0,
    }));
  };

  return (
    <SlideContainer>
      <EditorHeader>
        <Button
          disabled={isEditorLoading || isCampaignTemplatesLoading}
          label='Close editor'
          variant='secondary'
          onClick={() => handleCloseEditor()}
        />
      </EditorHeader>
      <MainContainer>
        <TextContainer>
          <Heading level={3} marginBottom='0px'>
            Get started
          </Heading>
          <Type px={14}>Choose a template.</Type>
        </TextContainer>
        <CardsContainer>
          {!!savedTemplateId && (
            <DisabledOverlay>
              <Type px={16} weight='semibold'>
                You aleady have a saved email template in progress. What would you like to do?
              </Type>
              <OverlayButtonsContainer>
                <Button buttonSize='small' label='Continue editing' onClick={handleContinueEditing} />
                <Button buttonSize='small' label='Delete and start over' variant='danger' onClick={handleStartOver} />
              </OverlayButtonsContainer>
            </DisabledOverlay>
          )}
          <ControlBar>
            <ControlGroup grow>
              <ControlSearchBox
                initialValue={searchFilter}
                placeholder='Search templates...'
                onChange={handleSearchFilterChange}
              />
            </ControlGroup>
          </ControlBar>

          {isPreExistingTemplatesLoading || isCampaignTemplatesLoading ? (
            <PageLoader />
          ) : (
            <>
              {rows.map((template) => {
                const hasTemplateContent = doesTemplateHaveContent(template);
                return (
                  <TemplateCard
                    key={template.Id}
                    selected={selectedListItem === template.Id}
                    onClick={() => handleItemClick(template)}
                  >
                    {selectedListItem === template.Id && (
                      <BlueCircleCheckmarkContainer data-testid='blue-circle-checkmark'>
                        <BlueCircleCheckmark />
                      </BlueCircleCheckmarkContainer>
                    )}
                    <TemplatePreview data-testid='template-preview' disabled={!hasTemplateContent}>
                      <HtmlPreviewIframe
                        sandbox='allow-same-origin'
                        srcDoc={
                          template.HtmlBody ||
                          '<h1 style="font-size: 48px; text-align: center; padding: 20px; color: #999;">No preview available</h1>'
                        }
                      />
                    </TemplatePreview>
                    <TemplateFooter>
                      <TemplateName>{template.TemplateName}</TemplateName>
                      {template.TemplateName !== 'Blank' && hasTemplateContent && (
                        <EyeButton data-testid='eye-button' onClick={() => setShowPreviewModal(true)}>
                          <EyeIcon />
                        </EyeButton>
                      )}
                    </TemplateFooter>
                  </TemplateCard>
                );
              })}
            </>
          )}

          {rows.length > 0 && !isCampaignTemplatesLoading && (
            <StyledPaginationControlContainer>
              <RebrandPagination
                count={campaignTemplates.TotalPages}
                page={currentPage + 1}
                onChange={(event, value) => handlePageChange(value - 1)}
              />
              <RightSideControls>
                <StatusContainer>
                  Displaying{' '}
                  <PaginationRange>
                    {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, campaignTemplates.TotalRows)}
                  </PaginationRange>{' '}
                  of <span>{campaignTemplates.TotalRows}</span>
                </StatusContainer>
                <FooterSelect
                  IconComponent={CaretDown}
                  value={pageSize.toString()}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {[5, 10, 25].map((size) => (
                    <MenuItem key={String(size)} value={String(size)}>
                      {size}
                    </MenuItem>
                  ))}
                </FooterSelect>
                <PageUnits>Per page</PageUnits>
              </RightSideControls>
            </StyledPaginationControlContainer>
          )}
        </CardsContainer>
      </MainContainer>
      <TemplatePreviewModal
        isOpen={showPreviewModal}
        templateHTML={selectedTemplateHTML ?? ''}
        onClose={() => setShowPreviewModal(false)}
      />
    </SlideContainer>
  );
}
const MainContainer = styled.div`
  background-color: var(--color-brand-primary-white);
  border-radius: 8px;
  width: 100%;
  max-width: 850px;
  padding: var(--sizes-70);
`;
const EditorHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--sizes-20);
  padding: var(--sizes-50) var(--sizes-60);
  background-color: white;
  border-bottom: 1px solid var(--color-gray-20);
  top: 0px;
`;
const TextContainer = styled.div`
  text-align: left;
  align-self: flex-start;
  margin-bottom: var(--sizes-70);
`;
const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sizes-50);
  position: relative;
`;
const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--color-gray-100);
  opacity: 0.85;
  z-index: 1;
  color: var(--color-brand-primary-white);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
`;
const OverlayButtonsContainer = styled.div`
  margin-top: var(--sizes-30);
  display: flex;
  gap: var(--sizes-40);
`;
const TemplateCard = styled.div<{ selected?: boolean }>`
  width: 248px;
  border: 1px solid var(--color-gray-20);
  outline: ${({ selected }) => (selected ? '3px solid var(--color-brand-secondary-sapphire)' : 'none')};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  position: relative;
`;
const BlueCircleCheckmarkContainer = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
`;
const EyeButton = styled.div`
  border-radius: 4px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-gray-70);
  width: 38px;
  height: 30px;

  :hover {
    background-color: var(--color-gray-60);
  }
`;
const TemplateFooter = styled.div`
  padding: var(--sizes-40);
  height: 54px;
  background-color: var(--color-brand-primary-white);
  border-top: 1px solid var(--color-gray-20);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const StyledPaginationControlContainer = styled(PaginationControlContainer)`
  border-top: 1px solid var(--color-gray-20);
  padding: var(--sizes-20);
  margin-top: var(--sizes-60);
  width: 100%;
  justify-content: space-between;
  padding-left: var(--sizes-100);
  padding-right: var(--sizes-100);

  @media (max-width: 1024px) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }

  @media (max-width: 640px) {
    margin-left: calc(-1 * var(--sizes-60));
    margin-right: calc(-1 * var(--sizes-60));
    width: calc(100% + 2 * var(--sizes-60));
  }
`;
